import React, { useEffect, useState } from 'react';
import type {
    BodyMeasurementOverrideType,
    PanelPlacementType,
    SizeChartAttributeType,
    SizeChartResultType,
} from 'src/types/decorationSpecs';
import { getSizeAttributeMm } from 'src/pages/editor/components/decorationSpecs/productPropertySizeChart';
import { formatMmWithInches, formatMm } from 'src/util/units';

export type DecorationSpecsProps = {
    isLoading: boolean;
    decorationPlacement: PanelPlacementType[] | null;
    printWhite: boolean | null;
    /** `null` means no size chart data is available for this product/selection — see
     * `productPropertySizeChart.ts` for how this is resolved (no longer from a separate fetch, so
     * there's no corresponding "ambiguous — missing a selection" state the way
     * `decorationMissingSelections` has; a cell whose value can't be resolved yet just renders as
     * "—", handled per-cell in `formatSizeChartCell`). */
    sizeChart: SizeChartResultType | null;
    /** Options DIS itself reports as still unresolved (e.g. `Backside`, `Decoration Technology`,
     * or `Size`) via a 400 response — see `disClient.ts`'s `retrieveDesignData`. */
    decorationMissingSelections: string[] | null;
    currentSize?: string;
    /** Name of the surface (e.g. `"Front"`, `"Back"`) the currently selected document is on, so
     * the matching decoration-placement row can be highlighted — see `getActiveSurfaceName.ts`. */
    activeSurfaceName?: string;
    /** Body Width/Length for `currentSize` as `getBodyMeasurements` currently resolves them —
     * already includes any designer override at top priority, so this is "what Phase B
     * auto-placement will actually use right now", not necessarily the computed/fallback value. */
    bodyWidthMm?: number;
    bodyLengthMm?: number;
    /** Non-null exactly when `bodyWidthMm`/`bodyLengthMm` above came from the designer's own
     * override rather than a computed source — see `getBodyMeasurementOverrideForCurrentSize`. */
    bodyMeasurementOverride: BodyMeasurementOverrideType | null;
    onSetBodyMeasurementOverride: (fields: { size: string; widthMm?: number; lengthMm?: number }) => void;
    onClearBodyMeasurementOverride: () => void;
};

/** Shown for a section instead of its data/"not available" note when the current selection
 * doesn't pin down every option that section's answer can vary by — e.g. `printWhite` genuinely
 * depends on `Substrate Color`, so answering while it's unset would just be picking one color's
 * answer for every color. */
function AmbiguousSelectionNote({ missing }: { missing: string[] }) {
    return <p className='decoration-specs-note'>Select {missing.join(', ')} above to see this.</p>;
}

function NotAvailableNote() {
    return <p className='decoration-specs-note'>Not available for this product.</p>;
}

/**
 * One small two-column (measurement, value) table per panel, rather than one wide table with a
 * column per measurement — a product usually only has 1-2 panels (Front/Back) but 4 measurements
 * (Distance from Collar, Width, Height, X Offset from Center), so a wide table needs far more
 * horizontal room than this drawer has (see `decoration-specs-table-scroll`'s own comment for what that
 * looked like), while a handful of narrow per-panel tables stacked vertically fits comfortably.
 */
function DecorationPlacementPanelTable({ panel, isActive }: { panel: PanelPlacementType; isActive: boolean }) {
    return (
        <div className={`decoration-specs-panel ${isActive ? 'decoration-specs-panel--active' : ''}`}>
            <h5 className='decoration-specs-table__title'>{panel.panelName}</h5>
            <table className='decoration-specs-table'>
                <thead>
                    <tr>
                        <th>Measurement</th>
                        <th>Value</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Distance from Collar</td>
                        <td>{formatMmWithInches(panel.distanceFromCollarToTopMm)}</td>
                    </tr>
                    <tr>
                        <td>Width</td>
                        <td>{formatMmWithInches(panel.scaledPrintWidthMm)}</td>
                    </tr>
                    <tr>
                        <td>Height</td>
                        <td>{formatMmWithInches(panel.scaledPrintHeightMm)}</td>
                    </tr>
                    <tr>
                        <td>X Offset from Center</td>
                        <td>{formatMmWithInches(panel.xOffsetFromCenterMm)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function DecorationPlacementTable({
    decorationPlacement,
    activeSurfaceName,
}: {
    decorationPlacement: PanelPlacementType[];
    activeSurfaceName?: string;
}) {
    return (
        <>
            {decorationPlacement.map((panel) => (
                <DecorationPlacementPanelTable
                    key={panel.panelName}
                    panel={panel}
                    isActive={panel.panelName === activeSurfaceName}
                />
            ))}
        </>
    );
}

/** Every size key present across a table's attributes, in first-seen order — normally every
 * attribute shares the same size keys, but this tolerates one that doesn't rather than dropping
 * data. */
function collectSizes(attributes: SizeChartAttributeType[]): string[] {
    const sizes: string[] = [];
    attributes.forEach((attribute) => {
        Object.keys(attribute.values).forEach((size) => {
            if (!sizes.includes(size)) {
                sizes.push(size);
            }
        });
    });
    return sizes;
}

/** Renders one size-chart cell as `<native value> (<mm equivalent>)`, e.g. `48 cm (480.0 mm)` or
 * `21 in (533.4 mm)` — mm is always the second unit shown (rather than "whichever unit isn't
 * native") so it lines up with decoration placement's mm-native values elsewhere on this same
 * panel, letting a designer cross-reference either section without converting by hand. */
function formatSizeChartCell(attribute: SizeChartAttributeType, size: string): string {
    const raw = attribute.values[size];
    if (raw === undefined) {
        return '—';
    }

    const alreadyHasUnitSuffix = /[a-zA-Z]/.test(raw);
    const primary = !alreadyHasUnitSuffix && attribute.unit ? `${raw} ${attribute.unit}` : raw;

    const mm = getSizeAttributeMm(attribute, size);
    if (mm === undefined) {
        return primary;
    }

    // If the native value is already specified in mm, avoid rendering redundant redundant "(425.0 mm)"
    const normalizedUnit = (
        alreadyHasUnitSuffix ? String(raw).replace(/[\d.\s]/g, '') : attribute.unit || ''
    ).toLowerCase();

    if (normalizedUnit === 'mm') {
        return primary;
    }

    return `${primary} (${formatMm(mm)})`;
}

/**
 * Sizes as rows, measurements as columns — swapped from the more conventional "sizes as columns"
 * print size-chart layout because this table lives in a narrow, fixed-width drawer (see
 * `decorationSpecsDrawer.tsx`): a product typically has more sizes (XS–4XL, say 7-8) than measurement
 * attributes (Length/Width/Sleeve, say 2-4), so rows-per-size scales far better in a narrow column
 * than one column per size would.
 */
function SizeChartTable({ attributes, currentSize }: { attributes: SizeChartAttributeType[]; currentSize?: string }) {
    const sizes = collectSizes(attributes);

    return (
        <div className='decoration-specs-table-scroll'>
            <table className='decoration-specs-table'>
                <thead>
                    <tr>
                        <th>Size</th>
                        {attributes.map((attribute) => (
                            <th key={attribute.attribute}>{attribute.attribute}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sizes.map((size) => (
                        <tr
                            key={size}
                            className={size === currentSize ? 'decoration-specs-table__row--active' : undefined}>
                            <td>{size}</td>
                            {attributes.map((attribute) => (
                                <td key={attribute.attribute}>{formatSizeChartCell(attribute, size)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** Parses a number input's string value into `number | undefined` — an empty string (the
 * designer cleared the field) becomes `undefined` rather than `NaN` or `0`, matching
 * `BodyMeasurementOverrideType`'s own "field present means override that measurement, absent
 * means defer to whatever `getBodyMeasurements` would otherwise resolve" convention. */
function parseOverrideInput(value: string): number | undefined {
    if (value.trim() === '') {
        return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

/** Rounds to 0.1mm — the precision the input displays at. Used for both the displayed value and
 * the "did the designer actually change anything" comparison below, so they agree: comparing a
 * rounded display value against an unrounded prop would otherwise create an override nobody
 * typed just from tabbing through an untouched field (e.g. a computed 501.65 displays as
 * "501.7", which never equals 501.65). */
function round1(value: number): number {
    return Math.round(value * 10) / 10;
}

/** One editable Width/Length input. Shows `value` (the currently resolved measurement — already
 * the override when one applies, per `getBodyMeasurements`' own priority order) rounded to 0.1mm,
 * and calls `onCommit` on blur with the parsed value (`undefined` when cleared) — not on every
 * keystroke, so this doesn't fire a Redux action per character typed, and not at all when the
 * rounded input still matches `value`, so blurring a field nobody edited is a no-op.
 *
 * Local state resyncs from `value` whenever it changes out from under it (switching Size, or a
 * background fetch resolving) via the `useEffect` below, rather than a key-based remount, so an
 * in-progress edit isn't lost to an unrelated re-render that happens to carry the same value. */
function MeasurementField({
    id,
    label,
    value,
    onCommit,
}: {
    id: string;
    label: string;
    value: number | undefined;
    onCommit: (next: number | undefined) => void;
}) {
    const [input, setInput] = useState('');

    useEffect(() => {
        setInput(value !== undefined ? String(round1(value)) : '');
    }, [value]);

    const commit = () => {
        const next = parseOverrideInput(input);
        if (next === (value !== undefined ? round1(value) : undefined)) {
            return;
        }
        onCommit(next);
    };

    return (
        <div className='decoration-specs-override__field'>
            <label htmlFor={id}>{label}</label>
            <input
                id={id}
                type='number'
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onBlur={commit}
                placeholder='—'
            />
        </div>
    );
}

/**
 * Lets a designer type in Width/Length (in mm) for the current size, overriding whatever
 * `getBodyMeasurements` would otherwise resolve from the CIM's own properties or the
 * linked-product fallback — the catch-all for both "neither source has a value" and "a source
 * has one but it's wrong for this garment". Deliberately a separate control from the Size Chart
 * table above rather than an inline edit on one of its cells: that table is a plain reflection of
 * what the CIM/linked-product sources say, so a designer can see the override's value right next
 * to the one it's replacing instead of it silently overwriting a cell with no record of what was
 * there before.
 */
function BodyMeasurementOverrideSection({
    currentSize,
    bodyWidthMm,
    bodyLengthMm,
    bodyMeasurementOverride,
    onSetBodyMeasurementOverride,
    onClearBodyMeasurementOverride,
}: Pick<
    DecorationSpecsProps,
    | 'currentSize'
    | 'bodyWidthMm'
    | 'bodyLengthMm'
    | 'bodyMeasurementOverride'
    | 'onSetBodyMeasurementOverride'
    | 'onClearBodyMeasurementOverride'
>) {
    if (!currentSize) {
        return null;
    }

    const isOverridden = !!bodyMeasurementOverride;

    return (
        <div className='decoration-specs-override'>
            <MeasurementField
                id='decoration-specs-override-width'
                label='Width (mm)'
                value={bodyWidthMm}
                onCommit={(widthMm) =>
                    onSetBodyMeasurementOverride({
                        size: currentSize,
                        widthMm,
                        lengthMm: bodyMeasurementOverride?.lengthMm,
                    })
                }
            />
            <MeasurementField
                id='decoration-specs-override-length'
                label='Length (mm)'
                value={bodyLengthMm}
                onCommit={(lengthMm) =>
                    onSetBodyMeasurementOverride({
                        size: currentSize,
                        widthMm: bodyMeasurementOverride?.widthMm,
                        lengthMm,
                    })
                }
            />
            {isOverridden && (
                <button
                    type='button'
                    className='decoration-specs-override__reset'
                    onClick={onClearBodyMeasurementOverride}>
                    Reset to computed value
                </button>
            )}
            {isOverridden && (
                <p className='decoration-specs-note'>Using manually entered values for size {currentSize}.</p>
            )}
        </div>
    );
}

/**
 * Displays apparel decoration-placement dimensions, white-ink state, and size chart data for the
 * currently selected product/variables — decoration placement/white-ink from the Design
 * Initialization Service, size chart resolved locally from the SKU picker's own product data (see
 * `productPropertySizeChart.ts`).
 *
 * Size Chart has its own, independent loading state (see this component's own `isLoading`
 * handling below) — it resolves synchronously alongside the SKU picker rather than via the async
 * fetch decoration placement/white ink depend on, so it isn't gated on that fetch's `isLoading` at
 * all. Decoration Placement/White Ink each show one of: a loading note, an "ambiguous — select
 * more options" prompt (when the current selection doesn't pin down every option that section's
 * answer can vary by), its data, or a "not available" note. Renders nothing at all for products
 * with no data, nothing ambiguous, and nothing loading (typically non-apparel) — there's no
 * product-type flag in Scene Maker to gate on, so absence of data is the gate.
 */
export default function DecorationSpecs({
    isLoading,
    decorationPlacement,
    printWhite,
    sizeChart,
    decorationMissingSelections,
    currentSize,
    activeSurfaceName,
    bodyWidthMm,
    bodyLengthMm,
    bodyMeasurementOverride,
    onSetBodyMeasurementOverride,
    onClearBodyMeasurementOverride,
}: DecorationSpecsProps) {
    const decorationAmbiguous = !!decorationMissingSelections && decorationMissingSelections.length > 0;
    const hasDecorationPlacement = !!decorationPlacement && decorationPlacement.length > 0;
    const hasSizeChart = !!sizeChart && sizeChart.attributes.length > 0;
    // The override section's own render gate (see `BodyMeasurementOverrideSection`) — checked here
    // too so a product with *no* computed data at all (the exact case the override exists to
    // catch) still shows the panel instead of being hidden by the guard below before a designer
    // ever gets the chance to enter one.
    const hasBodyMeasurementSection = !!currentSize;

    // Confirmed absent for this product/selection, nothing ambiguous, and not loading either
    // (typically non-apparel) — nothing to show. `isLoading` only ever covers
    // decorationPlacement/printWhite (see decorationSpecs/slice.ts) — it's checked here too so this
    // doesn't return `null` and skip rendering the loading state itself while a DIS fetch for a
    // product that turns out to have data is still in flight.
    if (
        !isLoading &&
        !decorationAmbiguous &&
        !hasDecorationPlacement &&
        !hasSizeChart &&
        printWhite === null &&
        !hasBodyMeasurementSection
    ) {
        return null;
    }

    return (
        <div className='decoration-specs-data'>
            {/* Size Chart first, and rendered independently of `isLoading` — it resolves from the
            SKU picker's own already-loaded data (see productPropertySizeChart.ts), not the DIS
            fetch decoration placement/white ink depend on, so it has nothing to wait on and stays
            in place across a DIS refetch instead of disappearing and reappearing. */}
            <h4>Size Chart</h4>
            {hasSizeChart ? (
                <SizeChartTable attributes={sizeChart!.attributes} currentSize={currentSize} />
            ) : (
                <NotAvailableNote />
            )}

            {/* Catch-all for when the Size Chart above is missing a value, or has one a designer
            knows is wrong for this garment — lets them enter Width/Length for the current size
            directly, taking priority over both the CIM's own properties and the linked-product
            fallback (see `getBodyMeasurements`). Shown whenever a size is selected, independent
            of whether the computed chart above has any data at all. */}
            {hasBodyMeasurementSection && (
                <>
                    <h4>Body Measurements ({currentSize})</h4>
                    <BodyMeasurementOverrideSection
                        currentSize={currentSize}
                        bodyWidthMm={bodyWidthMm}
                        bodyLengthMm={bodyLengthMm}
                        bodyMeasurementOverride={bodyMeasurementOverride}
                        onSetBodyMeasurementOverride={onSetBodyMeasurementOverride}
                        onClearBodyMeasurementOverride={onClearBodyMeasurementOverride}
                    />
                </>
            )}

            <h4>Decoration Placement</h4>
            {isLoading ? (
                <p className='decoration-specs-note'>Loading...</p>
            ) : decorationAmbiguous ? (
                <AmbiguousSelectionNote missing={decorationMissingSelections!} />
            ) : hasDecorationPlacement ? (
                <DecorationPlacementTable
                    decorationPlacement={decorationPlacement!}
                    activeSurfaceName={activeSurfaceName}
                />
            ) : (
                <NotAvailableNote />
            )}

            <h4>White Ink</h4>
            {isLoading ? (
                <p className='decoration-specs-note'>Loading...</p>
            ) : decorationAmbiguous ? (
                <AmbiguousSelectionNote missing={decorationMissingSelections!} />
            ) : (
                <p className='decoration-specs-note'>{printWhite === null ? 'Unknown' : printWhite ? 'Yes' : 'No'}</p>
            )}
        </div>
    );
}
